import React from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { useIntl } from '@olxpwa-lib/i18n/client';
import type { BookingCalendarFormValue } from '~components/AdServiceBooking/types';
import {
    Container,
    Row,
    StyledCostBox,
    StyledCostBoxCol,
    StyledCostBoxRow,
    StyledCostTitle
} from '~components/AdServiceBooking/CostCalculator';
import { TestId } from '~testId';
import { useFormContext } from 'react-hook-form';
import { P3, P4 } from '@olxui/core/dist/core/Typography';

export type CostCalculatorProps = {
    isStr?: boolean;
    price?: number;
    currencyCode?: string;
};

export const CostCalculator = ({
    isStr,
    currencyCode,
    price = 0
}: CostCalculatorProps) => {
    const { formatNumber, formatMessage } = useIntl();
    const { watch } = useFormContext<BookingCalendarFormValue>() ?? {};

    const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);

    const numberOfSelectedDays =
        dateFrom && dateTo ? differenceInCalendarDays(dateTo, dateFrom) + 1 : 1;

    const formattedPrice = formatNumber(price, {
        style: 'currency',
        currency: currencyCode
    });

    return (
        <Container data-testid={TestId.costCalculator}>
            <Row>
                <StyledCostTitle>
                    {formatMessage({
                        id: 'service.booking.ad.estimated.calculation.title',
                        defaultMessage: 'Estimated calculation'
                    })}
                </StyledCostTitle>
                <P4>
                    {formatMessage({
                        id: 'service.booking.ad.estimated.calculation.description',
                        defaultMessage:
                            'Prices may vary depending on several factors. These prices are an approximate estimate, you can negotiate and finalize the prices with the renter after your initial request.'
                    })}
                </P4>
            </Row>

            <StyledCostBox>
                <StyledCostBoxRow>
                    <StyledCostBoxCol>
                        <P4>
                            {formatMessage({
                                id: `service.booking.ad.estimated.calculation.${
                                    isStr ? 'price.per.night' : 'price.per.day'
                                }`,
                                defaultMessage: `Price per ${
                                    isStr ? 'night' : 'day'
                                }`
                            })}
                        </P4>
                    </StyledCostBoxCol>
                    <StyledCostBoxCol>
                        <P3
                            variant="bold"
                            data-testid={
                                isStr
                                    ? TestId.costCalculatorPricePerNight
                                    : TestId.costCalculatorPricePerDay
                            }
                        >
                            {formatMessage(
                                {
                                    id: 'service.booking.ad.estimated.calculation.from.value',
                                    defaultMessage: 'from { valueFrom }'
                                },
                                {
                                    valueFrom: formattedPrice
                                }
                            )}
                        </P3>
                    </StyledCostBoxCol>
                </StyledCostBoxRow>
                <StyledCostBoxRow>
                    <StyledCostBoxCol>
                        <P4>
                            {formatMessage({
                                id: `service.booking.ad.estimated.calculation.${
                                    isStr ? 'selected.nights' : 'selected.days'
                                }`,
                                defaultMessage: `Number of selected ${
                                    isStr ? 'nights' : 'days'
                                }`
                            })}
                        </P4>
                    </StyledCostBoxCol>
                    <StyledCostBoxCol>
                        <P3
                            variant="bold"
                            data-testid={
                                isStr
                                    ? TestId.costCalculatorNumOfSelectedNights
                                    : TestId.costCalculatorNumOfSelectedDays
                            }
                        >
                            {isStr
                                ? numberOfSelectedDays - 1
                                : numberOfSelectedDays}
                        </P3>
                    </StyledCostBoxCol>
                </StyledCostBoxRow>
            </StyledCostBox>
        </Container>
    );
};
